import type {
    IFormFieldValue,
    IResponseTiming,
    IResponseTimingInput,
} from "@agrigate/survey-api-def/lib/SurveysService/dto";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import { useCallback } from "react";
import type { SurveyQuestion } from "./SurveyBuilderForm";
import SurveyRunnerShell from "./SurveyRunnerShell";
import useMultiStepForm from "./utils/useMultiStepForm";

interface BaseResponse {
    id: string;
    value: string | string[];
}

export interface SurveyResponse extends BaseResponse {
    levelOneResponses?: LevelOneResponse[];
}

export interface LevelOneResponse extends BaseResponse {
    condition: string;
    levelTwoResponses?: LevelTwoResponse[];
}

interface LevelTwoResponse extends BaseResponse {
    condition: string;
}

const SurveyFormGenerator = (p: {
    surveyId: string;
    formName: string;
    questions: SurveyQuestion[];
    response?: MaybeN<IFormFieldValue[]>;
    timing?: MaybeN<IResponseTiming>;
    rewardCoins?: MaybeN<number>;
    onSubmit: (
        response: SurveyResponse[],
        timing: IResponseTimingInput,
        isComplete?: MaybeN<boolean>,
    ) => void;
    assetBaseUrl: string;
}) => {
    const {
        formName,
        questions,
        onSubmit,
        response,
        rewardCoins,
        surveyId,
        timing,
    } = p;
    const {
        step,
        isFirstStep,
        isLastStep,
        next,
        back,
        answers,
        currentBaseQuestionIndex,
        totalBaseQuestions,
        isCurrentQuestionAnswered,
        isCurrentQuestionRequired,
        readTiming,
    } = useMultiStepForm(questions, response, timing);

    const buildSurveyResponse = useCallback(
        (
            questions: SurveyQuestion[],
            answers: Record<string, string | string[]>,
        ): SurveyResponse[] =>
            questions.map((question): SurveyResponse => {
                const baseResponse: SurveyResponse = {
                    id: question.id,
                    value: answers[question.id] ?? "",
                    levelOneResponses: [],
                };

                if (question.levelOneFollowUpQuestions) {
                    question.levelOneFollowUpQuestions.forEach(levelOne => {
                        const baseAnswer = answers[question.id];
                        const condition = levelOne.condition?.id ?? "";
                        let isLevelOneConditionMet = false;

                        if (baseAnswer != null) {
                            isLevelOneConditionMet = Array.isArray(baseAnswer)
                                ? baseAnswer.includes(condition)
                                : baseAnswer === condition;
                        }

                        if (isLevelOneConditionMet) {
                            const levelOneResponse: LevelOneResponse = {
                                id: levelOne.id,
                                value: answers[levelOne.id] ?? "",
                                condition: levelOne.condition?.id ?? "",
                                levelTwoResponses: [],
                            };

                            if (levelOne.levelTwoFollowUpQuestions) {
                                levelOne.levelTwoFollowUpQuestions.forEach(
                                    levelTwo => {
                                        const levelOneAnswer =
                                            answers[levelOne.id];
                                        const levelTwoCondition =
                                            levelTwo.condition?.id ?? "";
                                        let isLevelTwoConditionMet = false;

                                        if (levelOneAnswer != null) {
                                            isLevelTwoConditionMet =
                                                Array.isArray(levelOneAnswer)
                                                    ? levelOneAnswer.includes(
                                                          levelTwoCondition,
                                                      )
                                                    : levelOneAnswer ===
                                                      levelTwoCondition;
                                        }

                                        if (isLevelTwoConditionMet) {
                                            levelOneResponse.levelTwoResponses?.push(
                                                {
                                                    id: levelTwo.id,
                                                    value:
                                                        answers[levelTwo.id] ??
                                                        "",
                                                    condition:
                                                        levelTwo.condition
                                                            ?.id ?? "",
                                                },
                                            );
                                        }
                                    },
                                );
                            }
                            baseResponse.levelOneResponses?.push(
                                levelOneResponse,
                            );
                        }
                    });
                }
                return baseResponse;
            }),
        [],
    );

    const handleSave = useCallback(
        (isComplete?: MaybeN<boolean>) => {
            onSubmit(
                buildSurveyResponse(questions, answers),
                readTiming(),
                isComplete,
            );
        },
        [buildSurveyResponse, questions, answers, onSubmit, readTiming],
    );

    const progressValue =
        ((currentBaseQuestionIndex + 1) / totalBaseQuestions) * 100;

    return (
        <SurveyRunnerShell
            formName={formName}
            step={step}
            progressValue={progressValue}
            progressLabel={`${currentBaseQuestionIndex + 1} of ${totalBaseQuestions}`}
            isFirstStep={isFirstStep}
            isLastStep={isLastStep}
            canAdvance={
                !(isCurrentQuestionRequired && !isCurrentQuestionAnswered)
            }
            rewardCoins={rewardCoins}
            onBack={back}
            onNext={() => {
                handleSave();
                next();
            }}
            onSubmit={() => handleSave(true)}
            onFlush={() => handleSave()}
        />
    );
};

export default SurveyFormGenerator;
